import React from "react";
import type { KonvaEventObject } from "konva/lib/Node";
import type { Stage as KonvaStage } from "konva/lib/Stage";

import type { SeatLayoutSeat } from "../../../api/services/seat-layout.service";
import {
  alignSeats,
  buildMultiSeatDragPreview,
  clampZoomLevel,
  distributeSeats,
  getCanvasBasePosition,
  getFitScale,
  normalizeSelectionRect,
  roundCanvasValue,
  seatIntersectsRect,
  type SeatAlignmentMode,
  type SeatDistributionMode,
} from "./geometry";
import type {
  LayoutCanvasOffset,
  LayoutCanvasViewport,
  LayoutSeatPosition,
  LayoutSelectionRect,
  LayoutStudioDraft,
  LayoutToolMode,
} from "./types";

type UseLayoutCanvasEditorParams = {
  draft: LayoutStudioDraft | null;
  setDraft: React.Dispatch<React.SetStateAction<LayoutStudioDraft | null>>;
};

export const useLayoutCanvasEditor = ({ draft, setDraft }: UseLayoutCanvasEditorParams) => {
  const [selectedSeatIds, setSelectedSeatIds] = React.useState<number[]>([]);
  const [selectionRect, setSelectionRect] = React.useState<LayoutSelectionRect | null>(null);
  const [canvasViewport, setCanvasViewport] = React.useState<LayoutCanvasViewport>({ width: 0, height: 0 });
  const [activeTool, setActiveTool] = React.useState<LayoutToolMode>("select");
  const [zoomLevel, setZoomLevel] = React.useState<number>(1);
  const [canvasOffset, setCanvasOffset] = React.useState<LayoutCanvasOffset>({ x: 0, y: 0 });
  const [dragPreviewPositions, setDragPreviewPositions] = React.useState<Record<number, LayoutSeatPosition>>({});

  const stageRef = React.useRef<KonvaStage | null>(null);
  const canvasViewportRef = React.useRef<HTMLDivElement | null>(null);
  const selectionAdditiveRef = React.useRef(false);
  const dragSnapshotRef = React.useRef<Map<number, LayoutSeatPosition>>(new Map());
  const isSeatDraggingRef = React.useRef(false);
  const isPanningRef = React.useRef(false);
  const panStartRef = React.useRef<{ pointerX: number; pointerY: number; offsetX: number; offsetY: number } | null>(null);

  const selectedSeatIdsSet = React.useMemo(() => new Set(selectedSeatIds), [selectedSeatIds]);
  const selectedSeats = React.useMemo(
    () => draft?.seats.filter((seat) => selectedSeatIdsSet.has(seat.spot)) || [],
    [draft, selectedSeatIdsSet],
  );

  const fitScale = React.useMemo(() => getFitScale(draft, canvasViewport), [canvasViewport, draft]);
  const canvasScale = React.useMemo(() => fitScale * zoomLevel, [fitScale, zoomLevel]);
  const canvasBasePosition = React.useMemo(
    () => getCanvasBasePosition(draft, canvasViewport, canvasScale),
    [canvasScale, canvasViewport, draft],
  );
  const canvasOrigin = React.useMemo(
    () => ({ x: canvasBasePosition.x + canvasOffset.x, y: canvasBasePosition.y + canvasOffset.y }),
    [canvasBasePosition.x, canvasBasePosition.y, canvasOffset.x, canvasOffset.y],
  );
  const stageWidth = Math.max(canvasViewport.width, 1);
  const stageHeight = Math.max(canvasViewport.height, 1);
  const canvasCursor =
    activeTool === "pan" ? (isPanningRef.current ? "grabbing" : "grab") : activeTool === "zoom" ? "zoom-in" : "default";

  React.useLayoutEffect(() => {
    const element = canvasViewportRef.current;
    if (!element) {
      return undefined;
    }

    const updateViewport = () => {
      setCanvasViewport({
        width: element.clientWidth,
        height: element.clientHeight,
      });
    };

    updateViewport();

    const observer = new ResizeObserver(updateViewport);
    observer.observe(element);
    return () => observer.disconnect();
  }, [draft]);

  React.useEffect(() => {
    if (activeTool !== "pan") {
      isPanningRef.current = false;
      panStartRef.current = null;
    }
  }, [activeTool]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "a") {
        if (!draft?.seats.length) {
          return;
        }
        event.preventDefault();
        setSelectedSeatIds(draft.seats.map((seat) => seat.spot));
      }

      if (event.key === "Escape") {
        setSelectedSeatIds([]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [draft]);

  const updateSeats = React.useCallback(
    (spotIds: number[], changes: Partial<SeatLayoutSeat>) => {
      const targetIds = new Set(spotIds);
      setDraft((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          seats: prev.seats.map((seat) => (targetIds.has(seat.spot) ? { ...seat, ...changes } : seat)),
        };
      });
    },
    [setDraft],
  );

  const updateSeat = React.useCallback(
    (spotId: number, changes: Partial<SeatLayoutSeat>) => {
      updateSeats([spotId], changes);
    },
    [updateSeats],
  );

  const transformSelectedSeats = React.useCallback(
    (transformer: (seats: SeatLayoutSeat[]) => SeatLayoutSeat[]) => {
      setDraft((prev) => {
        if (!prev) {
          return prev;
        }

        const currentSelectedSeats = prev.seats.filter((seat) => selectedSeatIdsSet.has(seat.spot));
        if (!currentSelectedSeats.length) {
          return prev;
        }

        const transformedSeats = transformer(currentSelectedSeats.map((seat) => ({ ...seat })));
        const transformedSeatMap = new Map(transformedSeats.map((seat) => [seat.spot, seat]));

        return {
          ...prev,
          seats: prev.seats.map((seat) => transformedSeatMap.get(seat.spot) || seat),
        };
      });
    },
    [selectedSeatIdsSet, setDraft],
  );

  const alignSelectedSeats = React.useCallback(
    (mode: SeatAlignmentMode) => {
      transformSelectedSeats((seats) => alignSeats(seats, mode));
    },
    [transformSelectedSeats],
  );

  const distributeSelectedSeats = React.useCallback(
    (mode: SeatDistributionMode) => {
      transformSelectedSeats((seats) => distributeSeats(seats, mode));
    },
    [transformSelectedSeats],
  );

  const getCanvasPointer = React.useCallback(() => {
    const stage = stageRef.current;
    if (!stage) {
      return null;
    }

    const pointer = stage.getPointerPosition();
    if (!pointer) {
      return null;
    }

    return {
      x: (pointer.x - canvasOrigin.x) / canvasScale,
      y: (pointer.y - canvasOrigin.y) / canvasScale,
    };
  }, [canvasOrigin.x, canvasOrigin.y, canvasScale]);

  const setZoomAtPoint = React.useCallback(
    (nextZoomLevel: number, pointer?: { x: number; y: number } | null) => {
      if (!draft) {
        return;
      }

      const clampedZoom = clampZoomLevel(nextZoomLevel);
      const effectiveViewport = {
        width: stageWidth,
        height: stageHeight,
      };
      const nextScale = getFitScale(draft, effectiveViewport) * clampedZoom;
      const nextBasePosition = getCanvasBasePosition(draft, effectiveViewport, nextScale);
      const targetPointer = pointer || {
        x: stageWidth / 2,
        y: stageHeight / 2,
      };
      const canvasPoint = {
        x: (targetPointer.x - canvasOrigin.x) / canvasScale,
        y: (targetPointer.y - canvasOrigin.y) / canvasScale,
      };

      setZoomLevel(clampedZoom);
      setCanvasOffset({
        x: targetPointer.x - canvasPoint.x * nextScale - nextBasePosition.x,
        y: targetPointer.y - canvasPoint.y * nextScale - nextBasePosition.y,
      });
    },
    [canvasOrigin.x, canvasOrigin.y, canvasScale, draft, stageHeight, stageWidth],
  );

  const resetCanvasView = React.useCallback(() => {
    setZoomLevel(1);
    setCanvasOffset({ x: 0, y: 0 });
  }, []);

  const handleSeatSelect = React.useCallback((spotId: number, event: KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (activeTool !== "select") {
      return;
    }
    event.cancelBubble = true;
    const isAdditive = event.evt.shiftKey || event.evt.metaKey || event.evt.ctrlKey;

    setSelectedSeatIds((prev) => {
      if (!isAdditive) {
        return [spotId];
      }

      if (prev.includes(spotId)) {
        return prev.filter((id) => id !== spotId);
      }

      return [...prev, spotId];
    });
  }, [activeTool]);

  const handleStageMouseDown = React.useCallback((event: KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (activeTool === "zoom") {
      const pointer = stageRef.current?.getPointerPosition();
      setZoomAtPoint(zoomLevel * (event.evt.shiftKey ? 0.85 : 1.15), pointer);
      return;
    }

    if (activeTool === "pan") {
      const pointer = stageRef.current?.getPointerPosition();
      if (!pointer) {
        return;
      }
      isPanningRef.current = true;
      panStartRef.current = {
        pointerX: pointer.x,
        pointerY: pointer.y,
        offsetX: canvasOffset.x,
        offsetY: canvasOffset.y,
      };
      return;
    }

    if (activeTool !== "select") {
      return;
    }

    if (isSeatDraggingRef.current) {
      return;
    }

    const target = event.target;
    const isStageTarget = target === target.getStage();
    const isCanvasBackground = target.hasName("canvas-background");

    if (!isStageTarget && !isCanvasBackground) {
      return;
    }

    const pointer = getCanvasPointer();
    if (!pointer) {
      return;
    }

    selectionAdditiveRef.current = event.evt.shiftKey || event.evt.metaKey || event.evt.ctrlKey;
    setSelectionRect({
      x: pointer.x,
      y: pointer.y,
      width: 0,
      height: 0,
      visible: true,
    });
  }, [activeTool, canvasOffset.x, canvasOffset.y, getCanvasPointer, setZoomAtPoint, zoomLevel]);

  const handleStageMouseMove = React.useCallback(() => {
    if (activeTool === "pan") {
      if (!isPanningRef.current || !panStartRef.current) {
        return;
      }

      const pointer = stageRef.current?.getPointerPosition();
      if (!pointer) {
        return;
      }

      setCanvasOffset({
        x: panStartRef.current.offsetX + (pointer.x - panStartRef.current.pointerX),
        y: panStartRef.current.offsetY + (pointer.y - panStartRef.current.pointerY),
      });
      return;
    }

    if (activeTool !== "select") {
      return;
    }

    if (!selectionRect?.visible) {
      return;
    }

    const pointer = getCanvasPointer();
    if (!pointer) {
      return;
    }

    setSelectionRect((prev) =>
      prev
        ? {
            ...prev,
            width: pointer.x - prev.x,
            height: pointer.y - prev.y,
          }
        : prev,
    );
  }, [activeTool, getCanvasPointer, selectionRect?.visible]);

  const handleStageMouseUp = React.useCallback(() => {
    if (activeTool === "pan") {
      isPanningRef.current = false;
      panStartRef.current = null;
      return;
    }

    if (activeTool !== "select") {
      return;
    }

    if (isSeatDraggingRef.current) {
      return;
    }

    if (!selectionRect?.visible) {
      return;
    }

    const normalizedRect = normalizeSelectionRect(selectionRect);
    const hasDragged = normalizedRect.width > 6 || normalizedRect.height > 6;

    if (!hasDragged) {
      if (!selectionAdditiveRef.current) {
        setSelectedSeatIds([]);
      }
      setSelectionRect(null);
      return;
    }

    const matchedSeatIds = draft?.seats
      .filter((seat) => seatIntersectsRect(seat, normalizedRect))
      .map((seat) => seat.spot) || [];
    setSelectedSeatIds((prev) =>
      selectionAdditiveRef.current ? Array.from(new Set([...prev, ...matchedSeatIds])) : matchedSeatIds,
    );
    setSelectionRect(null);
  }, [activeTool, draft?.seats, selectionRect]);

  const handleStageWheel = React.useCallback((event: KonvaEventObject<WheelEvent>) => {
    if (activeTool !== "zoom") {
      return;
    }

    event.evt.preventDefault();
    const pointer = stageRef.current?.getPointerPosition();
    const nextZoom = event.evt.deltaY < 0 ? zoomLevel * 1.08 : zoomLevel / 1.08;
    setZoomAtPoint(nextZoom, pointer);
  }, [activeTool, setZoomAtPoint, zoomLevel]);

  const getRenderedSeatPosition = React.useCallback(
    (seat: SeatLayoutSeat): LayoutSeatPosition => dragPreviewPositions[seat.spot] || { x: seat.x, y: seat.y },
    [dragPreviewPositions],
  );

  const handleSeatPointerDown = React.useCallback((seat: SeatLayoutSeat, event: KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (activeTool !== "select") {
      return;
    }

    event.cancelBubble = true;

    const isAdditive = event.evt.shiftKey || event.evt.metaKey || event.evt.ctrlKey;
    setSelectedSeatIds((prev) => {
      if (prev.includes(seat.spot)) {
        return prev;
      }

      if (isAdditive) {
        return [...prev, seat.spot];
      }

      return [seat.spot];
    });
  }, [activeTool]);

  const handleSeatDragStart = React.useCallback((seat: SeatLayoutSeat) => {
    isSeatDraggingRef.current = true;

    if (!selectedSeatIdsSet.has(seat.spot) || selectedSeatIds.length < 2) {
      dragSnapshotRef.current = new Map([[seat.spot, { x: seat.x, y: seat.y }]]);
      setSelectedSeatIds((prev) => (prev.includes(seat.spot) ? prev : [seat.spot]));
      setDragPreviewPositions({});
      return;
    }

    dragSnapshotRef.current = new Map(
      selectedSeats.map((selectedSeat) => [selectedSeat.spot, { x: selectedSeat.x, y: selectedSeat.y }]),
    );
    setDragPreviewPositions(
      Object.fromEntries(
        selectedSeats.map((selectedSeat) => [selectedSeat.spot, { x: selectedSeat.x, y: selectedSeat.y }]),
      ),
    );
  }, [selectedSeatIds.length, selectedSeatIdsSet, selectedSeats]);

  const handleSeatDragMove = React.useCallback((seat: SeatLayoutSeat, event: KonvaEventObject<DragEvent>) => {
    const nextX = roundCanvasValue(event.target.x());
    const nextY = roundCanvasValue(event.target.y());

    if (!selectedSeatIdsSet.has(seat.spot) || selectedSeatIds.length < 2) {
      setDragPreviewPositions({
        [seat.spot]: { x: nextX, y: nextY },
      });
      return;
    }

    setDragPreviewPositions(
      buildMultiSeatDragPreview({
        seats: selectedSeats,
        draggedSeat: seat,
        dragSnapshot: dragSnapshotRef.current,
        nextPosition: { x: nextX, y: nextY },
      }),
    );
  }, [selectedSeatIds.length, selectedSeatIdsSet, selectedSeats]);

  const handleSeatDragEnd = React.useCallback((seat: SeatLayoutSeat, event: KonvaEventObject<DragEvent>) => {
    const nextX = roundCanvasValue(event.target.x());
    const nextY = roundCanvasValue(event.target.y());
    const origin = dragSnapshotRef.current.get(seat.spot);

    isSeatDraggingRef.current = false;

    if (!origin || !selectedSeatIdsSet.has(seat.spot) || selectedSeatIds.length < 2) {
      updateSeat(seat.spot, { x: nextX, y: nextY });
      setDragPreviewPositions({});
      return;
    }

    const deltaX = nextX - origin.x;
    const deltaY = nextY - origin.y;
    transformSelectedSeats((seats) =>
      seats.map((selectedSeat) => {
        const selectedOrigin = dragSnapshotRef.current.get(selectedSeat.spot);
        return selectedOrigin
          ? {
              ...selectedSeat,
              x: roundCanvasValue(selectedOrigin.x + deltaX),
              y: roundCanvasValue(selectedOrigin.y + deltaY),
            }
          : selectedSeat;
      }),
    );
    setDragPreviewPositions({});
  }, [selectedSeatIds.length, selectedSeatIdsSet, transformSelectedSeats, updateSeat]);

  const handleSeatDragCancel = React.useCallback(() => {
    isSeatDraggingRef.current = false;
    setDragPreviewPositions({});
  }, []);

  const resetEditorState = React.useCallback(() => {
    setSelectedSeatIds([]);
    setSelectionRect(null);
    setActiveTool("select");
    setZoomLevel(1);
    setCanvasOffset({ x: 0, y: 0 });
    setDragPreviewPositions({});
    dragSnapshotRef.current = new Map();
    selectionAdditiveRef.current = false;
    isSeatDraggingRef.current = false;
    isPanningRef.current = false;
    panStartRef.current = null;
  }, []);

  return {
    selectedSeatIds,
    setSelectedSeatIds,
    selectedSeatIdsSet,
    selectedSeats,
    selectionRect,
    canvasViewportRef,
    stageRef,
    activeTool,
    setActiveTool,
    zoomLevel,
    canvasScale,
    canvasOrigin,
    stageWidth,
    stageHeight,
    canvasCursor,
    updateSeat,
    updateSeats,
    alignSelectedSeats,
    distributeSelectedSeats,
    setZoomAtPoint,
    resetCanvasView,
    resetEditorState,
    handleSeatSelect,
    handleStageMouseDown,
    handleStageMouseMove,
    handleStageMouseUp,
    handleStageWheel,
    getRenderedSeatPosition,
    handleSeatPointerDown,
    handleSeatDragStart,
    handleSeatDragMove,
    handleSeatDragEnd,
    handleSeatDragCancel,
  };
};
